import {
  BookOpenIcon,
  BuildingOffice2Icon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  CpuChipIcon,
  CurrencyDollarIcon,
  DevicePhoneMobileIcon,
  GiftIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  TicketIcon,
  UsersIcon,
  DocumentCheckIcon,
  PhotoIcon,
  QuestionMarkCircleIcon,
  QueueListIcon
} from '@heroicons/react/24/outline';
import { t } from '../i18n';
import { hasPagePermission, isPlatformAdmin, canManageSubTenants } from '../utils/permissions';

export const systemManagementModules = [
  {
    id: 'organization-user',
    title: t('organization_user_management'),
    description: t('organization_user_management_desc'),
    icon: UsersIcon,
    path: '/system-management/organization-user',
    permission: 'PAGE:SYSTEM:ORG'
  },
  {
    id: 'department',
    title: t('department_management'),
    description: t('department_management_desc'),
    icon: BuildingOffice2Icon,
    path: '/department',
    permission: 'PAGE:SYSTEM:ORG'
  },
  {
    id: 'tenant',
    title: t('tenant_management_module'),
    description: t('tenant_management_desc'),
    icon: ShieldCheckIcon,
    path: '/system-management/tenant',
    permission: 'PAGE:SYSTEM:TENANT'
  },
  {
    id: 'iot-device-assignment',
    title: 'IoT 设备分配',
    description: '将平台池中的传感设备分配给租户，分配后仅该租户可见',
    icon: DevicePhoneMobileIcon,
    path: '/system-management/iot-device-assignment',
    platformAdminOnly: true
  },
  {
    id: 'operation-log',
    title: t('operation_log_module'),
    description: t('operation_log_desc'),
    icon: ClipboardDocumentListIcon,
    path: '/system-management/operation-log'
  },
  {
    id: 'experience-review',
    title: '经验共创审核',
    description: '审核用户提交的植保/治疗经验，通过后发放激励',
    icon: DocumentCheckIcon,
    path: '/system-management/experience-review'
  },
  {
    id: 'work-tasks',
    title: '工作任务管理',
    description: '员工日报审核、任务分配发布、AI 验收与队列监控',
    icon: QueueListIcon,
    path: '/system-management/work-tasks'
  },
  {
    id: 'home-operation-slots',
    title: '首页运营位配置',
    description: '配置欢迎页轮播图与专题位，保存后前台即时展示',
    icon: PhotoIcon,
    path: '/system-management/home-operation-slots'
  },
  {
    id: 'finance',
    title: t('finance_management'),
    description: t('finance_management_desc'),
    icon: CurrencyDollarIcon,
    path: '/system-management/finance-management'
  },
  {
    id: 'mall-statistics',
    title: '商城经营看板',
    description: '商城销售 KPI、趋势、热销与支付异常，口径对齐财务接口',
    icon: ChartBarIcon,
    path: '/system-management/mall-statistics'
  },
  {
    id: 'mall-coupons',
    title: '优惠券管理',
    description: '配置商城券模板，支撑小程序领券与下单核销',
    icon: TicketIcon,
    path: '/system-management/mall/coupons'
  },
  {
    id: 'mall-points-rules',
    title: '积分规则配置',
    description: '配置赠送/抵现规则，参与结算预览与支付赠分',
    icon: GiftIcon,
    path: '/system-management/mall/points-rules'
  },
  {
    id: 'mall-campaigns',
    title: '营销活动审批',
    description: '活动上架申请与审核，对标专票审批流程',
    icon: MegaphoneIcon,
    path: '/system-management/mall/campaigns'
  },
  {
    id: 'security',
    title: t('security_settings_module'),
    description: t('security_settings_desc'),
    icon: ShieldCheckIcon,
    path: '/system-management/security-settings'
  },
  {
    id: 'ai',
    title: t('ai_settings_module'),
    description: t('ai_settings_desc'),
    icon: CpuChipIcon,
    path: '/system-management/ai-settings'
  },
  {
    id: 'dict',
    title: t('dict_management'),
    description: t('dict_management_desc'),
    icon: BookOpenIcon,
    path: '/system-management/dict-management'
  },
  {
    id: 'param-help',
    title: '参数帮助管理',
    description: '维护各模块问号提示的参数说明、公式与示例',
    icon: QuestionMarkCircleIcon,
    path: '/system-management/param-help'
  },
  {
    id: 'menu-membership',
    title: t('menu_membership_module'),
    description: t('menu_membership_module_desc'),
    icon: Cog6ToothIcon,
    path: '/menu-membership-management'
  }
];

export function matchSystemManagementModule(item, pathname) {
  if (item.id === 'organization-user') {
    const prefixes = [
      '/system-management/organization-user',
      '/system-management/organization',
      '/system-management/user',
      '/system-management/role',
      '/system-management/permission',
      '/system-management/page-permission',
      '/system-management/user-role',
      '/system-management/role-permission',
      '/system-management/tenant-member-applications'
    ];
    return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  if (item.id === 'ai') {
    const prefixes = [
      '/system-management/ai-settings',
      '/system-management/ai-model-config',
      '/system-management/ai-prompt-config',
      '/system-management/prompt-assets',
      '/system-management/prompt-ops',
      '/system-management/cache-management'
    ];
    return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
  }
  if (pathname === item.path) return true;
  if (item.path.startsWith('/system-management/') && pathname.startsWith(`${item.path}/`)) {
    return true;
  }
  return false;
}

export function findSystemManagementModule(pathname) {
  let matched = null;
  for (const item of systemManagementModules) {
    if (matchSystemManagementModule(item, pathname)) {
      if (!matched || item.path.length > matched.path.length) {
        matched = item;
      }
    }
  }
  return matched;
}

/** 按页面权限过滤系统管理子模块（侧栏/横向导航） */
export function getVisibleSystemManagementModules() {
  return systemManagementModules.filter((item) => {
    if (item.platformAdminOnly && !isPlatformAdmin()) {
      return false;
    }
    if (item.id === 'tenant') {
      return isPlatformAdmin() || hasPagePermission('PAGE:SYSTEM:SUB_TENANT');
    }
    return !item.permission || hasPagePermission(item.permission);
  });
}
